import { useState } from 'react';
import { useStore } from '@nanostores/react';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import type { PaymentCard } from '@/services/billing/types';
import type { BillingFieldsForm } from '../components/add-funds-billing-fields';
import { addressFromAccount, applyAddressChange, visibleBillingErrors } from '../utils/address-form-helpers';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { billingFieldErrors } from '../utils/credit-card-form-validation';
import { useBillingEditGuard } from './use-billing-edit-guard';
import { useStripeLoad, type StripeLoadState } from './use-stripe-load';

export type AddonCardEntry = BillingFieldsForm & {
  hasSavedCard: boolean;
  cardDetails: PaymentCard | null;
  useSavedCard: boolean;
  isFormOpen: boolean;
  isCardComplete: boolean;
  stripeLoad: StripeLoadState;
  onUseNewCard: () => void;
  onUseSavedCard: () => void;
  onOpenForm: () => void;
  onCardChange: (isComplete: boolean) => void;
  onTouchFields: (fields: readonly string[]) => void;
};

export function useAddonCardEntry(): AddonCardEntry {
  const { account } = useAuth();
  const hasSavedCard = useStore(billing.hasCard);
  const cardDetails = useStore(billing.cardDetails);
  const editGuard = useBillingEditGuard();
  const stripeLoad = useStripeLoad();

  const [useSavedCard, setUseSavedCard] = useState(hasSavedCard);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState<CreditCardFormAddress>(() => addressFromAccount(account));
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());

  return {
    hasSavedCard,
    cardDetails,
    useSavedCard,
    isFormOpen,
    isCardComplete,
    stripeLoad,
    name,
    address,
    visibleErrors: visibleBillingErrors(billingFieldErrors(name, address), touched),
    canEdit: editGuard.canEdit,
    onNameInput: setName,
    onAddressChange: (field, value) => setAddress((current) => applyAddressChange(current, field, value)),
    onFieldBlur: (field) => setTouched((current) => new Set(current).add(field)),
    onUseNewCard: () => {
      setUseSavedCard(false);
      setIsFormOpen(true);
    },
    onUseSavedCard: () => {
      setUseSavedCard(true);
      setIsFormOpen(false);
    },
    onOpenForm: () => setIsFormOpen(true),
    onCardChange: setIsCardComplete,
    onTouchFields: (fields) => setTouched((current) => new Set([...current, ...fields])),
  };
}
